import { useCallback, useEffect, useMemo, useState } from 'react';
import constants from '../../config/constants';
import { CookieStorage } from '../CookieStorage';
import { WebStorage } from '../WebStorage';
import { useDeliveryOptionsMetrics } from './useDeliveryOptionsMetrics';

const { AVAILABILITY_PICKUPINSTORE, AVAILABILITY_STOCKINSTORE, AVAILABILITY_HOMEDELIVERY } = constants;
const SELECTED_HD = 'SELECTED_HD';
const SELECTED_CC = 'SELECTED_CC';

export const setToWebStorage = (key, regionObj) => {
  WebStorage.setItem({
    name: key,
    value: JSON.stringify(regionObj),
    shouldPersist: true,
  });
};

export const getFromWebStorage = (key) => {
  try {
    return JSON.parse(
      WebStorage.getItem({
        name: key,
        fromPersistence: true,
      })
    );
  } catch (err) {
    return null;
  }
};

export const getFromCookie = (key) => {
  const cookieVal = CookieStorage.getCookie(key);
  const parsedCookieVal = CookieStorage.parseCookieValue(key, cookieVal);
  return parsedCookieVal;
};

const useDeliveryDataPersistence = ({ store, selectedZone, regionCode, currentVariantId }) => {
  const {
    handleSetEntry,
    handleSetCounts,
    handleSetEntryGeoData,
    handleChangedGeoData,
    clearMetricsData,
    resetAllMetricsData,
  } = useDeliveryOptionsMetrics();
  const [pickupInStoreData, setPickupInStoreDataState] = useState([]);
  const [homeDeliveryData, setHomeDeliveryDataState] = useState(null);
  const [storesList, setStoresListState] = useState([]);
  const [selectedType, setSelectedType] = useState('');
  const [selectedStoreId, setSelectedStoreId] = useState({
    PickupInStore: null,
    StockInStore: null,
  });
  const [isDataSetForMetric, setIsDataSetForMetric] = useState(false);
  const [textGenerated, setTextGenerated] = useState({
    StockInStore: {},
    HomeDelivery: {},
    PickupInStore: {},
  });
  const [selectedDeliveryOption, setSelectedDeliveryOption] = useState({
    PickupInStore: {},
    HomeDelivery: {},
    StockInStore: {},
  });

  const [deliveryErrors, setDeliveryErrors] = useState({
    PickupInStore: { isError: false, code: null },
    HomeDelivery: { isError: false, code: null },
    StockInStore: { isError: false, code: null },
  });

  const setPickupInStoreData = useCallback((data) => {
    setPickupInStoreDataState(data);
  }, []);

  const setHomeDeliveryData = useCallback((data) => {
    setHomeDeliveryDataState(data);
  }, []);

  const setStoresList = useCallback((data) => {
    setStoresListState(data);
  }, []);

  const saveSelectedDeliveryOption = useCallback(
    (option) => {
      const optionToSave = { ...option, currentVariantId };
      const { isError, deliveryOptionType } = optionToSave;
      const key = deliveryOptionType === 'HomeDelivery' ? SELECTED_HD : SELECTED_CC;

      if (deliveryOptionType === 'HomeDelivery' && !isError && selectedZone?.parentStateName) {
        optionToSave.parentStateName = selectedZone.parentStateName;
      }

      CookieStorage.setCookie({
        name: key,
        value: optionToSave,
        regionCode,
        store,
      });
      setToWebStorage(key, optionToSave);
      setSelectedDeliveryOption((prev) => ({
        ...prev,
        [deliveryOptionType]: optionToSave,
      }));
    },
    [currentVariantId, regionCode, selectedZone, store]
  );

  const clearDeliveryData = useCallback(({ type, isCambiar = false }) => {
    setPickupInStoreDataState(null);
    setHomeDeliveryDataState(null);
    setStoresListState([]);
    setSelectedStoreId({
      PickupInStore: null,
      StockInStore: null,
    });
    setIsDataSetForMetric(false);
    setSelectedDeliveryOption({
      PickupInStore: {},
      HomeDelivery: {},
      StockInStore: {},
    });
    clearMetricsData({ type, isCambiar });
  }, []);

  useEffect(() => {
    if (isDataSetForMetric) {
      resetAllMetricsData();
      clearDeliveryData({ type: selectedType, isCambiar: false });
    }
  }, [currentVariantId]);

  const setDeliveryError = useCallback((deliveryType, hasError, errorCode = null) => {
    const typeMap = {
      [AVAILABILITY_PICKUPINSTORE]: 'PickupInStore',
      [AVAILABILITY_HOMEDELIVERY]: 'HomeDelivery',
      [AVAILABILITY_STOCKINSTORE]: 'StockInStore',
    };

    const errorKey = typeMap[deliveryType];
    if (!errorKey) return;

    setDeliveryErrors((prev) => ({
      ...prev,
      [errorKey]: {
        isError: hasError,
        code: hasError ? errorCode : null,
      },
    }));
  }, []);

  const clearDeliveryErrors = useCallback(() => {
    setDeliveryErrors({
      PickupInStore: { isError: false, code: null },
      HomeDelivery: { isError: false, code: null },
      StockInStore: { isError: false, code: null },
    });
  }, []);

  const deliveryData = useMemo(
    () => ({
      pickupInStoreData,
      homeDeliveryData,
      storesList,
      selectedStoreId,
    }),
    [pickupInStoreData, homeDeliveryData, storesList, selectedStoreId]
  );

  useEffect(() => {
    if (isDataSetForMetric) {
      if (homeDeliveryData !== null && homeDeliveryData.length > 0) {
        handleSetCounts(AVAILABILITY_PICKUPINSTORE, pickupInStoreData.length);
        handleSetCounts(AVAILABILITY_HOMEDELIVERY, homeDeliveryData.length);
        handleSetCounts(AVAILABILITY_STOCKINSTORE, storesList.length);
      } else if (storesList.length > 0) {
        handleSetCounts(AVAILABILITY_STOCKINSTORE, storesList.length);
        handleSetCounts(AVAILABILITY_HOMEDELIVERY, 0);
        handleSetCounts(AVAILABILITY_PICKUPINSTORE, 0);
      } else {
        handleSetCounts(AVAILABILITY_STOCKINSTORE, 0);
        handleSetCounts(AVAILABILITY_HOMEDELIVERY, 0);
        handleSetCounts(AVAILABILITY_PICKUPINSTORE, 0);
      }
    }
  }, [homeDeliveryData, storesList.length]);

  const deliveryActions = useMemo(
    () => ({
      setPickupInStoreData,
      setHomeDeliveryData,
      setStoresList,
      clearDeliveryData,
      setTextGenerated,
      setDeliveryError,
      clearDeliveryErrors,
      saveSelectedDeliveryOption,
      setSelectedStoreId,
      handleSetEntry,
      setIsDataSetForMetric,
      handleSetEntryGeoData,
      handleChangedGeoData,
    }),
    [
      setPickupInStoreData,
      setHomeDeliveryData,
      setStoresList,
      clearDeliveryData,
      setTextGenerated,
      setDeliveryError,
      clearDeliveryErrors,
      saveSelectedDeliveryOption,
      setSelectedStoreId,
      handleSetEntry,
      setIsDataSetForMetric,
      handleSetEntryGeoData,
      handleChangedGeoData,
    ]
  );

  return {
    deliveryData,
    deliveryActions,
    selectedType,
    setSelectedType,
    textGenerated,
    deliveryErrors,
    selectedDeliveryOption,
    setSelectedDeliveryOption: saveSelectedDeliveryOption,
  };
};

export default useDeliveryDataPersistence;
